const PREVIEW_HOST_PATH = "/preview-host/index.html";
const DEFAULT_LOADER_URL = "https://layer.accelasearch.io/V2/loader.js";

export class PreviewPopupBlockedError extends Error {
  constructor() {
    super("The preview window was blocked by the browser");
    this.name = "PreviewPopupBlockedError";
  }
}

interface PreviewEnv {
  VITE_LOADER_URL?: string;
}

export function getPreviewLoaderUrl({
  env = import.meta.env as PreviewEnv,
}: { env?: PreviewEnv } = {}): string {
  return env.VITE_LOADER_URL || DEFAULT_LOADER_URL;
}

function hostUrl(origin: string, state?: string): URL {
  const url = new URL(PREVIEW_HOST_PATH, origin);
  if (state) url.searchParams.set("state", state);
  return url;
}

type PreviewWindow = Pick<Window, "location"> & { opener?: unknown };

function navigatePreview(previewWindow: PreviewWindow, url: string): void {
  if (typeof previewWindow.location?.replace === "function") previewWindow.location.replace(url);
  else (previewWindow as { location: unknown }).location = url;
}

export interface PreviewOptions {
  origin?: string;
  loaderUrl?: string;
}

/** Salva la config in localStorage e costruisce l'URL della pagina host, come il legacy. */
export function preparePreview(
  config: unknown,
  { origin = window.location.origin, loaderUrl = getPreviewLoaderUrl() }: PreviewOptions = {},
): string {
  localStorage.setItem("config", JSON.stringify(config));
  const params = btoa(JSON.stringify({ loaderUrl, config: "localStorage" }));
  const previewUrl = hostUrl(origin);
  previewUrl.searchParams.set("params", params);
  return previewUrl.toString();
}

export interface OpenPreviewOptions extends PreviewOptions {
  openWindow?: (url: string) => PreviewWindow | null;
}

/** Apre subito la finestra (i browser consentono il popup solo nel click), poi la porta sull'anteprima. */
export async function openPreview(
  buildConfig: () => Promise<unknown> | unknown,
  {
    origin = window.location.origin,
    loaderUrl = getPreviewLoaderUrl(),
    openWindow = (url) => window.open(url, "_blank"),
  }: OpenPreviewOptions = {},
): Promise<string> {
  const previewWindow = openWindow(hostUrl(origin, "preparing").toString());
  if (!previewWindow) throw new PreviewPopupBlockedError();
  try {
    const config = await buildConfig();
    const previewUrl = preparePreview(config, { origin, loaderUrl });
    navigatePreview(previewWindow, previewUrl);
    try {
      previewWindow.opener = null;
    } catch {
      /* finestra cross-origin */
    }
    return previewUrl;
  } catch (error) {
    navigatePreview(previewWindow, hostUrl(origin, "error").toString());
    throw error;
  }
}
